import { useState } from 'react';
import {
  Paper,
  Button,
  Group,
  Stack,
  Text,
  Modal,
  Checkbox,
  Divider,
  Anchor,
} from '@mantine/core';
import { useIntl } from 'react-intl';
import {
  useCookieConsent,
  type CookiePreferences,
} from '@/contexts/CookieConsentContext';
import classes from './CookieConsentBanner.module.css';

export const CookieConsentBanner = () => {
  const intl = useIntl();
  const {
    showBanner,
    preferences,
    acceptAll,
    declineAll,
    savePreferences,
    isSettingsOpen,
    openSettings,
    closeSettings,
  } = useCookieConsent();

  const [tempPreferences, setTempPreferences] =
    useState<CookiePreferences>(preferences);

  const handleOpenSettings = () => {
    setTempPreferences(preferences);
    openSettings();
  };

  if (!showBanner && !isSettingsOpen) {
    return null;
  }

  return (
    <>
      {/* Cookie Banner - Hide when modal is open */}
      {showBanner && !isSettingsOpen && (
        <Paper
          className={classes.banner}
          shadow="lg"
          p="md"
          withBorder
          radius="md"
        >
          <Stack gap="md">
            <Text size="sm" fw={500}>
              {intl.formatMessage({ id: 'cookieConsentTitle' })}
            </Text>
            <Text size="xs" c="dimmed">
              {intl.formatMessage({ id: 'cookieConsentDescription' })}{' '}
              <Anchor
                href="#"
                size="xs"
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenSettings();
                }}
              >
                {intl.formatMessage({ id: 'learnMore' })}
              </Anchor>
            </Text>
            <Group gap="xs" justify="flex-end" wrap="wrap">
              <Button variant="subtle" size="xs" onClick={declineAll}>
                {intl.formatMessage({ id: 'decline' })}
              </Button>
              <Button variant="outline" size="xs" onClick={handleOpenSettings}>
                {intl.formatMessage({ id: 'customize' })}
              </Button>
              <Button size="xs" onClick={acceptAll}>
                {intl.formatMessage({ id: 'acceptAll' })}
              </Button>
            </Group>
          </Stack>
        </Paper>
      )}

      {/* Cookie Settings Modal */}
      <Modal
        opened={isSettingsOpen}
        onClose={closeSettings}
        title={intl.formatMessage({ id: 'cookieSettings' })}
        size="lg"
        centered
        zIndex={20000}
        overlayProps={{
          style: { zIndex: 19999 },
        }}
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            {intl.formatMessage({ id: 'cookieSettingsDescription' })}
          </Text>

          <Divider />

          {/* Essential Cookies */}
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <Stack gap={4} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>
                  {intl.formatMessage({ id: 'essentialCookies' })}
                </Text>
                <Text size="xs" c="dimmed">
                  {intl.formatMessage({ id: 'essentialCookiesDescription' })}
                </Text>
              </Stack>
              <Checkbox checked={true} disabled size="sm" />
            </Group>
          </Stack>

          <Divider />

          {/* Preferences Cookies */}
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <Stack gap={4} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>
                  {intl.formatMessage({ id: 'preferencesCookies' })}
                </Text>
                <Text size="xs" c="dimmed">
                  {intl.formatMessage({ id: 'preferencesCookiesDescription' })}
                </Text>
              </Stack>
              <Checkbox
                checked={tempPreferences.preferences}
                onChange={(e) =>
                  setTempPreferences({
                    ...tempPreferences,
                    preferences: e.currentTarget.checked,
                  })
                }
                size="sm"
              />
            </Group>
          </Stack>

          <Divider />

          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={closeSettings}>
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
            <Button variant="outline" onClick={declineAll}>
              {intl.formatMessage({ id: 'declineAll' })}
            </Button>
            <Button onClick={() => savePreferences(tempPreferences)}>
              {intl.formatMessage({ id: 'savePreferences' })}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
